import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Trend } from '@viome/viome-base/src/usecase/Health/Health.model';
import { ScoreReferenceData } from '@viome/viome-base/src/usecase/Health/Health.model';
export interface VGraphData {
  color: string;
  date: string;
  message: string;
  score: number;
  sequence_no: number;
}
interface VGraphProps {
  data: VGraphData[];
  height?: number;
  reverse?: boolean;
  referenceData?: ScoreReferenceData;
}

const getColorRange = (range: number) => {
  return 100 - range;
};

const getColorRangesYellow = (high_range: number, low_range: number) => {
  return (high_range + low_range) / 2;
};

const getLowRangeByRank = (
  rank: string,
  referenceData?: ScoreReferenceData,
) => {
  const foundRange = referenceData?.score_range.find(
    (item) => item.rank === rank,
  );
  return foundRange ? foundRange.low_range : null;
};

const getHighRangeByRank = (
  rank: string,
  referenceData?: ScoreReferenceData,
) => {
  const foundRange = referenceData?.score_range.find(
    (item) => item.rank === rank,
  );
  return foundRange ? foundRange.high_range : null;
};

const filterRanges = (ranges: number[]) => {
  return ranges.filter((num, _, arr) => !arr.includes(num + 1));
};

const sortData = (data: VGraphData[]) => {
  return data
    .map((d) => ({
      ...d,
      parsedDate: d3.timeParse('%Y-%m-%d')(d.date) as Date,
    }))
    .sort((a, b) => a.sequence_no - b.sequence_no);
};

export const VGraph: React.FC<VGraphProps> = ({
  data,
  height = 300,
  reverse = false,
  referenceData,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);
  const [selectedPoint, setSelectedPoint] = useState<Trend | null>(null);

  const flattenedRanges = referenceData?.score_range.flatMap(
    ({ high_range, low_range }) => [high_range, low_range],
  );

  const filteredRanges = filterRanges(flattenedRanges || []);

  useEffect(() => {
    // Dynamically set the width based on the parent container
    const resizeObserver = new ResizeObserver((entries) => {
      const containerWidth = entries[0]?.contentRect?.width || 0;
      setWidth(containerWidth);
    });

    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }

    return () => {
      if (chartRef.current) resizeObserver.unobserve(chartRef.current);
    };
  }, []);

  useEffect(() => {
    const sortedData = sortData(data);
    setSelectedPoint(sortedData[0] ?? []);
  }, [data]);

  useEffect(() => {
    if (!data || data.length === 0 || width === 0) return;

    // Clear previous chart
    d3.select(chartRef.current).selectAll('*').remove();

    // Define Dimensions
    const margin = { top: 20, right: 50, bottom: 50, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Parse Dates
    const parsedData = sortData(data);

    const lastDataPoint = parsedData[0];

    const halfwayScore = reverse
      ? lastDataPoint.score / 2
      : (lastDataPoint.score + 100) / 2;
    const projectionDate1 = new Date(lastDataPoint.parsedDate);
    const projectionDate2 = new Date(lastDataPoint.parsedDate);
    projectionDate1.setDate(projectionDate1.getDate() + 0.3);
    projectionDate2.setDate(projectionDate2.getDate() + 15);

    const extenddData = [
      ...parsedData,
      {
        ...lastDataPoint,
        score: lastDataPoint.score,
        parsedDate: projectionDate1,
      },
      { ...lastDataPoint, score: halfwayScore, parsedDate: projectionDate2 },
    ];

    const extendedData = [...parsedData];

    const bottomRange = getColorRange(
      getHighRangeByRank('Attention', referenceData) || 0,
    );

    const topRange = getColorRange(
      getLowRangeByRank('Maintain', referenceData) || 0,
    );

    const middleRange = getColorRangesYellow(bottomRange, topRange);

    // Create Scales
    const xScale = d3
      .scaleTime()
      .domain(d3.extent(extendedData, (d) => d.parsedDate) as [Date, Date])
      .range([0, innerWidth]);

    const yScale = d3.scaleLinear().domain([0, 100]).range([innerHeight, 0]);

    // Create SVG Container
    const svg = d3
      .select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const defs = svg.append('defs');

    defs
      .append('linearGradient')
      .attr('id', 'backgroundGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%')
      .selectAll('stop')
      .data([
        {
          offset: reverse ? '25%' : `${topRange}%`,
          color: reverse ? '#FF3A3A' : '#00CA9F',
        },
        {
          offset: reverse ? '40%' : `${middleRange - 3}%`,
          color: '#FFAB01',
        },
        {
          offset: reverse ? '60%' : `${middleRange + 3}%`,
          color: '#FFAB01',
        },
        {
          offset: reverse ? '75%' : `${bottomRange}%`,
          color: reverse ? '#00CA9F' : '#FF3A3A',
        },
      ])
      .enter()
      .append('stop')
      .attr('offset', (d) => d.offset)
      .attr('stop-color', (d) => d.color);

    // TODO: Sample code for testing backgroundGradient background
    // svg
    //   .append('rect')
    //   .attr('x', 0)
    //   .attr('y', 0)
    //   .attr('width', innerWidth)
    //   .attr('height', innerHeight)
    //   .attr('fill', 'url(#backgroundGradient)');

    const horizontalLines = [...filteredRanges];
    svg
      .selectAll('.horizontal-line')
      .data(horizontalLines)
      .enter()
      .append('line')
      .attr('class', 'horizontal-line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', '#ccc')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4 4')
      .attr('stroke-opacity', 0.75);

    const mask = defs.append('mask').attr('id', 'lineMask');

    mask
      .append('path')
      .datum(extendedData)
      .attr(
        'd',
        d3
          .line<{ parsedDate: Date; score: number }>()
          .x((d) => xScale(d.parsedDate))
          .y((d) => yScale(d.score))
          .curve(d3.curveMonotoneX) as any,
      )
      .attr('stroke', 'white')
      .attr('stroke-width', 4)
      .attr('fill', 'none');

    svg
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'url(#backgroundGradient)')
      .attr('mask', 'url(#lineMask)');

    // Add Projection Line
    const projectionLine = d3
      .line<{ parsedDate: Date; score: number }>()
      .x((d) => xScale(d.parsedDate))
      .y((d) => yScale(d.score))
      .curve(d3.curveMonotoneX);

    svg
      .append('path')
      .datum(extenddData.slice(-2))
      .attr('d', projectionLine as any)
      .attr('stroke', '#ccc')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4 4')
      .attr('fill', 'none')
      .attr('stroke-opacity', 0.5);

    // Add Labels for Data Points
    svg
      .selectAll('.data-label')
      .data(parsedData)
      .enter()
      .append('text')
      .attr('class', 'data-label')
      .attr('x', (d) => xScale(d.parsedDate))
      .attr('y', (d) => yScale(d.score) - 10)
      .attr('text-anchor', 'middle')
      .attr('fill', (d) => d.color)
      .text((d) => d.message);

    // Add Points
    svg
      .selectAll('.data-point')
      .data(parsedData)
      .enter()
      .append('circle')
      .attr('class', 'data-point')
      .attr('cx', (d) => xScale(d.parsedDate))
      .attr('cy', (d) => yScale(d.score))
      .attr('r', 5)
      .attr('fill', (d) => d.color)
      .on('click', (_, d) => setSelectedPoint(d));

    if (selectedPoint) {
      const selectedDate = d3.timeParse('%Y-%m-%d')(selectedPoint.date) as Date;
      const labelText = d3.timeFormat('%b %d, %Y')(selectedDate);

      svg
        .append('line')
        .attr('class', 'hover-line')
        .attr('x1', xScale(selectedDate))
        .attr('x2', xScale(selectedDate))
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', '#ccc')
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', 0.1);

      // Add a group to encapsulate the label and background rect
      const labelGroup = svg.append('g').attr('class', 'hover-label-group');

      // Measure text size
      const textElement = labelGroup
        .append('text')
        .attr('x', xScale(selectedDate))
        .attr('y', 0) // Position above the graph
        .attr('text-anchor', 'middle')
        .attr('class', 'hover-label')
        .attr('fill', '#fff')
        .attr('font-size', '12px')
        .text(labelText);

      const textWidth = textElement.node()?.getBBox().width || 0;
      const textHeight = textElement.node()?.getBBox().height || 0;

      // Add a background rect behind the text
      labelGroup
        .insert('rect', 'text')
        .attr('x', xScale(selectedDate) - textWidth / 2 - 8)
        .attr('y', 0 - textHeight - 1)
        .attr('width', textWidth + 16)
        .attr('height', textHeight + 8)
        .attr('rx', 4)
        .attr('fill', '#2F2F2F')
        .attr('opacity', 0.9);
    }
  }, [
    data,
    width,
    height,
    selectedPoint,
    reverse,
    filteredRanges,
    referenceData,
  ]);

  return <div ref={chartRef} style={{ width: '100%' }}></div>;
};
